import React, { useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { always, length, lt, pipe, map } from 'ramda';
import NextLink from 'next/link';
import { useEffectOnce } from 'react-use';
import dayjs from 'dayjs';

import { renderWhen, renderWhenTrue } from '@utils/rendering';
import { FavoriteButton } from '@components/favoriteButton';
import { formatCartPrice, getFullArtistName } from '@utils/helpers';
import { useBreakpoint } from '@hooks/useBreakpoint';
import { ROUTE } from '@utils/constants';
import { useHeaderSublevel } from '@hooks/useUi';
import { PhysicalItem, RemoveCartPayload } from '@definitions/shoppingCart.types';

import {
  Container,
  ImageWrapper,
  Image,
  Details,
  QuantityContainer,
  Quantity,
  RemoveButton,
  NameContainer,
  Artist,
  ProductTitle,
  PriceContainer,
  Price,
  VariantOption,
  ExpiringDate,
} from './shoppingCartItem.styles';

export interface ShoppingCartPhysicalItemProps {
  physicalItem: PhysicalItem;
  removeItem: (data: RemoveCartPayload) => void;
  index: number;
}

export const ShoppingCartPhysicalItem = ({
  physicalItem: {
    productId,
    variantId,
    imageUrl,
    artist,
    name,
    year,
    salePrice,
    slug,
    favorite,
    optionValues,
    id,
    liveEndAt,
  },
  removeItem,
  index,
}: ShoppingCartPhysicalItemProps) => {
  const { isMobile } = useBreakpoint();
  const isBeforeTwoMonths = dayjs(liveEndAt).isBefore(dayjs().utc().add(60, 'day'));
  const linkRef = useRef<HTMLAnchorElement>(null);
  const { closeHeaderSublevel } = useHeaderSublevel();
  const renderPrice = renderWhenTrue(() => <Price>{!!salePrice && formatCartPrice(salePrice)}</Price>);
  const renderRemoveButton = renderWhenTrue(
    always(
      <RemoveButton onClick={() => removeItem({ itemId: id })}>
        <FormattedMessage id="shoppingCartItem.removeButton" defaultMessage="Remove" />
      </RemoveButton>
    )
  );

  const renderOptions = renderWhen(
    pipe(length, lt(0)),
    map(({ optionDisplayName, label }) => (
      <VariantOption key={label}>
        {optionDisplayName}: {label}
      </VariantOption>
    ))
  );

  const renderExpirationInformation = renderWhenTrue(
    always(
      <ExpiringDate>
        <FormattedMessage
          id="shoppingCartItem.expiringDate"
          defaultMessage="Available until {date}."
          values={{ date: dayjs(liveEndAt).format('h:mm A, MMM D, YYYY') }}
        />
      </ExpiringDate>
    )
  );

  const mainUrl = `${ROUTE.ARTWORKS}/${artist.slug}/${slug}`;

  const cartItemLink = optionValues?.length ? `${mainUrl}?variantId=${variantId}` : mainUrl;

  useEffectOnce(() => {
    if (!index) {
      linkRef.current?.focus();
    }
  });

  return (
    <Container key={productId} data-testid={name}>
      <Details>
        <QuantityContainer>
          <Quantity>1</Quantity>
          {renderRemoveButton(!isMobile)}
        </QuantityContainer>
        <NameContainer>
          <Artist>{getFullArtistName(artist)}</Artist>
          <NextLink href={cartItemLink} passHref>
            <ProductTitle>
              <i>{name}</i>, {year}
            </ProductTitle>
          </NextLink>

          {renderExpirationInformation(isBeforeTwoMonths)}
          {renderOptions(optionValues)}
          {renderRemoveButton(isMobile)}
        </NameContainer>
        <PriceContainer>
          <FavoriteButton favorite={favorite} slug={slug} height={18} />
          {renderPrice(!!salePrice)}
        </PriceContainer>
      </Details>
      <NextLink href={cartItemLink} passHref>
        <ImageWrapper onClick={closeHeaderSublevel} ref={linkRef}>
          <Image src={imageUrl} alt={imageUrl} />
        </ImageWrapper>
      </NextLink>
    </Container>
  );
};
